import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import Modal from 'react-modal';
import './PDFExportSettings.scss';
import { generatePDFPreview } from './pdf/pdf';
import { ExportData } from './types';
import { IconClose } from '../../../components/Icons';

export type PDFOrientation = 'portrait' | 'landscape';

export interface PDFExportOptions {
  orientation: PDFOrientation;
  showHeaderOnAllPages: boolean;
}

interface PDFExportSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  onSavePDF?: (options: PDFExportOptions) => void;
  onPrint?: (options: PDFExportOptions) => void;
  exportData?: ExportData | null;
  defaultColumnCount?: number;
}

const PDFExportSettings: React.FC<PDFExportSettingsProps> = ({
  isOpen,
  onClose,
  onSavePDF,
  exportData,
  defaultColumnCount = 0,
}) => {
  // Default to landscape when budget is enabled (more columns to display)
  const hasBudget = exportData?.budgetAvailable === true;
  const [orientation, setOrientation] = useState<PDFOrientation>('landscape');
  const [showHeaderOnAllPages, setShowHeaderOnAllPages] = useState(true);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedRef = useRef(false);

  // Set initial orientation synchronously before paint when modal opens
  useLayoutEffect(() => {
    if (isOpen && exportData && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      const defaultOrientation = hasBudget || defaultColumnCount > 6 ? 'landscape' : 'portrait';
      setOrientation(defaultOrientation);
    }
    if (!isOpen) {
      hasInitializedRef.current = false;
    }
  }, [isOpen, exportData, hasBudget, defaultColumnCount]);

  const getOptions = useCallback(
    (): PDFExportOptions => ({
      orientation,
      showHeaderOnAllPages,
    }),
    [orientation, showHeaderOnAllPages]
  );

  // Generate PDF preview when options change (debounced)
  useEffect(() => {
    if (!exportData || !isOpen) return;

    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    setIsGenerating(true);

    // Debounce the PDF generation
    debounceTimerRef.current = setTimeout(() => {
      try {
        setError(null); // Clear previous error
        const options = getOptions();
        const url = generatePDFPreview(exportData, options);
        setPdfUrl(url);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to generate PDF preview';
        setError(errorMessage);
        setPdfUrl(null);
      } finally {
        setIsGenerating(false);
      }
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [exportData, isOpen, orientation, showHeaderOnAllPages, getOptions]);

  // Clear PDF URL when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPdfUrl(null);
    }
  }, [isOpen]);

  const handleSavePDF = () => {
    onSavePDF?.(getOptions());
  };

  return (
    <Modal
      ariaHideApp={false}
      id="pdf-export-settings-modal"
      isOpen={isOpen}
      onRequestClose={onClose}
      className="pdf-modal"
      overlayClassName="pdf-modal-overlay"
    >
      {/* Header */}
      <header className="pdf-modal__header">
        <div className="pdf-modal__header-left">
          <h2 className="pdf-modal__title">Print or save as PDF</h2>
          <p className="pdf-modal__subtitle">To print, right-click the preview and select Print</p>
        </div>
        <button className="pdf-modal__close-btn" onClick={onClose} aria-label="Close" type="button">
          <IconClose />
        </button>
      </header>

      {/* Content */}
      <div className="pdf-modal__content">
        {/* Sidebar */}
        <aside className="pdf-modal__sidebar">
          <h3 className="pdf-modal__sidebar-title">Report Print Settings</h3>

          <div className="pdf-modal__field">
            <label className="pdf-modal__label">Orientation</label>
            <select
              className="pdf-modal__select"
              value={orientation}
              onChange={e => setOrientation(e.target.value as PDFOrientation)}
            >
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
            </select>
          </div>

          <div className="pdf-modal__checkbox-group">
            <input
              type="checkbox"
              id="show-headers"
              className="pdf-modal__checkbox"
              checked={showHeaderOnAllPages}
              onChange={e => setShowHeaderOnAllPages(e.target.checked)}
            />
            <label htmlFor="show-headers" className="pdf-modal__checkbox-label">
              Show report headers on all pages
            </label>
          </div>
        </aside>

        {/* Preview Area */}
        <div className="pdf-modal__preview">
          {/* PDF Preview */}
          <div className="pdf-modal__preview-scroll">
            {isGenerating && (
              <div className="pdf-modal__loading">
                <div className="pdf-modal__loading-spinner"></div>
                <span>Generating preview...</span>
              </div>
            )}
            {!isGenerating && pdfUrl && (
              <object data={pdfUrl} type="application/pdf" className="pdf-modal__iframe" aria-label="PDF Preview">
                <p>
                  PDF preview not available.{' '}
                  <a href={pdfUrl} download="report.pdf">
                    Download PDF
                  </a>
                </p>
              </object>
            )}
            {!isGenerating && error && (
              <div className="pdf-modal__error">
                <span className="material-icons-outlined">error_outline</span>
                <p>Failed to generate preview</p>
                <p className="pdf-modal__error-detail">{error}</p>
              </div>
            )}
            {!isGenerating && !pdfUrl && !error && (
              <div className="pdf-modal__no-data">
                <span className="material-icons-outlined">description</span>
                <p>{exportData ? 'Preview unavailable' : 'No report data available'}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="pdf-modal__footer">
        <button className="pdf-modal__btn pdf-modal__btn--secondary" onClick={handleSavePDF} type="button">
          Save as PDF
        </button>
      </footer>
    </Modal>
  );
};

export default PDFExportSettings;
